import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessageAction } from '@shared/enums/entity-name.enum';
import { NewsMessageInterface } from '@shared/interfaces/news-message.interface';
import {
  Post,
  PostEngagementReport,
  PostStatistics,
} from '@shared/interfaces/post.interface';
import { Observable } from 'rxjs';
import { BaseHttpService } from './base-http.service';

export type EngagementReportGranularity =
  | 'Day'
  | 'Week'
  | 'Month'
  | 'Quarter'
  | 'Year';

@Injectable({
  providedIn: 'root',
})
export class NewsMessagesHttpService extends BaseHttpService<NewsMessageInterface> {
  constructor(protected http: HttpClient) {
    super(http);
  }

  getApiRootPath(): string {
    return 'news';
  }

  getCounts(): Observable<number> {
    const url = `${this.apiUrl}/counts`;
    return this.http.get<any>(url);
  }

  createPost(payload: Post): Observable<void> {
    return this.http.post<void>(this.apiUrl, payload);
  }

  editPost(id: number, payload: Post): Observable<void> {
    const dtoArray = [];

    for (const key of Object.keys(payload)) {
      dtoArray.push({
        value: payload[key],
        path: key,
        op: 'replace',
      });
    }

    return this.http.patch<void>(`${this.apiUrl}/${id}/post`, dtoArray);
  }

  tranformData(item: NewsMessageInterface): NewsMessageInterface {
    const actualReplies = item?.replies?.filter(
      reply => reply.action !== MessageAction.AddPersonToThread
    );
    const lastActualReply = actualReplies?.at(-1);

    if (lastActualReply) {
      item.latestMessage = lastActualReply;
    }
    return item;
  }

  deletePost(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}/post`);
  }

  incrementView(id: number): Observable<void> {
    const url = `${this.apiUrl}/${id}/views`;

    return this.http.post<void>(url, {});
  }

  getPostStatistics(id: number): Observable<PostStatistics> {
    const url = `${this.apiUrl}/${id}/statistics`;

    return this.http.get<PostStatistics>(url);
  }

  getEngagementReport(
    startDate: Date,
    endDate: Date,
    granularity: EngagementReportGranularity,
    postIds?: number[]
  ): Observable<PostEngagementReport[]> {
    const url = `${this.apiUrl}/engagement-report`;
    const params = this.buildEngagementReportParams(
      startDate,
      endDate,
      granularity,
      postIds
    );

    return this.http.get<PostEngagementReport[]>(url, { params });
  }

  exportEngagementReport(
    startDate: Date,
    endDate: Date,
    granularity: EngagementReportGranularity,
    postIds?: number[]
  ): Observable<string> {
    const url = `${this.apiUrl}/engagement-report/export`;
    const params = this.buildEngagementReportParams(
      startDate,
      endDate,
      granularity,
      postIds
    );

    return this.http.get(url, { params, responseType: 'text' });
  }

  private buildEngagementReportParams(
    startDate: Date,
    endDate: Date,
    granularity: EngagementReportGranularity,
    postIds?: number[]
  ): HttpParams {
    let params = new HttpParams()
      .set('startDate', startDate.toISOString())
      .set('endDate', endDate.toISOString())
      .set('granularity', granularity);

    for (const id of postIds ?? []) {
      params = params.append('postIds', id);
    }

    return params;
  }
}
